import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

interface SystemMessageCardProps {
  id: string;
  title: string;
  body: string;
  createdAt: string;
  className?: string;
}

const SystemMessageCard: React.FC<SystemMessageCardProps> = ({
  id,
  title,
  body,
  createdAt,
  className = ""
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  
  const shouldShowExpandButton = body.length > 150;
  const displayBody = isExpanded ? body : body.slice(0, 150) + (shouldShowExpandButton ? '...' : '');

  return (
    <Card className={`transition-all duration-200 hover:shadow-md w-full ${className}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <CardTitle className="text-lg font-semibold text-foreground break-words">{title}</CardTitle>
          <div className="flex items-center text-muted-foreground text-sm ml-4">
            <Clock className="w-4 h-4 mr-1" />
            {format(new Date(createdAt), 'MMM dd, HH:mm')}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap break-words">
          {displayBody}
        </p>
        {shouldShowExpandButton && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="mt-3 p-0 h-auto text-primary hover:text-primary/80"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="w-4 h-4 mr-1" />
                {t('dashboard:collapse')}
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4 mr-1" />
                {t('dashboard:expand')}
              </>
            )}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default SystemMessageCard;